(() => {
  const NEWSLETTER_PENDING = 'sc:clovr-club:newsletter-pending';
  const DETAILS_PENDING = 'sc:clovr-club:details-pending';
  const JOINED = 'sc:clovr-club:joined';
  const WELCOME_POSTER_SUBSCRIBED = 'sc:welcome-poster:subscribed';

  const countryTagHandle = (value) => value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  const countryInterestTags = ({ baseTags, optedIn, countryChoice, otherCountry }) => {
    if (!optedIn) return baseTags;
    const country = countryChoice === 'OTHER' ? otherCountry : countryChoice;
    const countryTag = countryTagHandle(country || '');
    if (!countryTag || countryTag === 'other') return baseTags;
    return `${baseTags},delivery-interest,delivery-interest-country-${countryTag}`;
  };

  const requestedClubPanel = (trigger) => (
    trigger?.dataset?.scClovrClubPanel === 'signup' ? 'signup' : null
  );

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // Native form submission remains functional when storage is blocked.
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Nothing to clean up when storage is unavailable.
      }
    },
  };

  const cleanResultUrl = (formId, queryKey) => {
    try {
      const url = new URL(window.location.href);
      if (url.hash === `#${formId}`) url.hash = '';
      url.searchParams.delete(queryKey);
      window.history.replaceState(window.history.state, '', url.href);
    } catch {
      // URL cleanup is cosmetic; never block the form result.
    }
  };

  // SC_MODAL_FOCUS_TOOLS_START
  const SC_MODAL_FOCUSABLE = [
    'a[href]',
    'button',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  const modalFocusables = (dialog) => (
    [...dialog.querySelectorAll(SC_MODAL_FOCUSABLE)].filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.matches(':disabled, [hidden]')) return false;
      if (element.tabIndex < 0) return false;
      if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    })
  );

  const retainModalFocus = (dialog) => {
    const previousTabindex = dialog.getAttribute('tabindex');
    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
    if (previousTabindex === null) dialog.removeAttribute('tabindex');
    else dialog.setAttribute('tabindex', previousTabindex);
  };

  const containModalTab = (dialog, event) => {
    if (event.key !== 'Tab') return false;
    const focusables = modalFocusables(dialog);
    if (!focusables.length) {
      event.preventDefault();
      retainModalFocus(dialog);
      return true;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const focusIsInvalid = active === dialog || !dialog.contains(active) || !focusables.includes(active);
    if (event.shiftKey && (focusIsInvalid || active === first)) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (focusIsInvalid || active === last)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
    return true;
  };
  // SC_MODAL_FOCUS_TOOLS_END

  const init = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.scClubReady === 'true') return;

    const dialog = root.querySelector('[data-sc-club-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;

    root.dataset.scClubReady = 'true';
    const panels = Array.from(root.querySelectorAll('[data-sc-club-panel]'));
    const newsletterForm = root.querySelector('[data-sc-club-newsletter-form]');
    const detailsForm = root.querySelector('[data-sc-club-details-form]');
    const newsletterState = root.querySelector('[data-sc-club-newsletter-state]');
    const detailsState = root.querySelector('[data-sc-club-details-state]');
    let returnFocus = null;
    let pendingReturnFocus = null;
    let nativeSubmissionPrepared = false;
    let nativeSubmissionPending = false;
    let nativeSubmissionKey = null;
    let ignoreNextCloseEvent = false;
    let applyDeliveryInterestFromTrigger = () => {};

    const setupCountryInterest = () => {
      if (!(newsletterForm instanceof HTMLFormElement)) return;

      const consent = newsletterForm.querySelector('[data-sc-club-delivery-interest]');
      const fields = newsletterForm.querySelector('[data-sc-club-country-interest]');
      const countryChoice = newsletterForm.querySelector('[data-sc-club-country-choice]');
      const otherField = newsletterForm.querySelector('[data-sc-club-other-country-field]');
      const otherCountry = newsletterForm.querySelector('[data-sc-club-other-country]');
      const tagsInput = newsletterForm.querySelector('[data-sc-club-tags]');
      if (!(consent instanceof HTMLInputElement)
        || !(fields instanceof HTMLElement)
        || !(countryChoice instanceof HTMLSelectElement)
        || !(otherField instanceof HTMLElement)
        || !(otherCountry instanceof HTMLSelectElement)
        || !(tagsInput instanceof HTMLInputElement)) return;

      const baseTags = tagsInput.dataset.baseTags || 'newsletter,clovr-club';
      Array.from(otherCountry.options).forEach((option) => {
        if (option.value === '---' || option.value === 'Malaysia' || option.value === 'Singapore') {
          option.disabled = true;
          option.hidden = true;
        }
      });

      const sync = () => {
        const optedIn = consent.checked;
        const isOther = optedIn && countryChoice.value === 'OTHER';
        fields.hidden = !optedIn;
        countryChoice.disabled = !optedIn;
        countryChoice.required = optedIn;
        countryChoice.setAttribute('aria-required', String(optedIn));
        otherField.hidden = !isOther;
        otherCountry.disabled = !isOther;
        otherCountry.required = isOther;
        otherCountry.setAttribute('aria-required', String(isOther));
        consent.setAttribute('aria-expanded', String(optedIn));
        tagsInput.value = countryInterestTags({
          baseTags,
          optedIn,
          countryChoice: countryChoice.value,
          otherCountry: otherCountry.value,
        });
      };

      applyDeliveryInterestFromTrigger = (trigger) => {
        if (!(trigger instanceof HTMLElement)) return;
        const countryIso = (trigger.dataset.scClovrClubCountryIso || '').trim().toUpperCase();
        const countryName = (trigger.dataset.scClovrClubCountryName || '').trim();
        if (!countryIso || !countryName || countryIso === 'MY') return;

        // Do not overwrite a country the visitor already selected manually.
        if (consent.checked && countryChoice.value) return;

        const directChoice = Array.from(countryChoice.options)
          .find((option) => option.value === countryName && !option.disabled);
        if (directChoice) {
          countryChoice.value = directChoice.value;
          otherCountry.value = '';
        } else {
          const otherChoice = Array.from(otherCountry.options)
            .find((option) => option.value === countryName && !option.disabled);
          if (!otherChoice) return;
          countryChoice.value = 'OTHER';
          otherCountry.value = otherChoice.value;
        }

        // Prefill the country without changing either consent choice. The
        // visitor must opt in to delivery updates explicitly.
        sync();
      };

      consent.addEventListener('change', sync);
      countryChoice.addEventListener('change', sync);
      otherCountry.addEventListener('change', sync);
      sync();
    };

    const showPanel = (name) => {
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.scClubPanel !== name;
      });
      const activePanel = panels.find((panel) => panel.dataset.scClubPanel === name);
      const activeTitle = activePanel?.querySelector('.sc-club__title[id]');
      if (activeTitle) dialog.setAttribute('aria-labelledby', activeTitle.id);
    };

    const open = (trigger, panel = storage.get(JOINED) === 'true' ? 'details' : 'signup') => {
      if (dialog.open) return;
      returnFocus = trigger instanceof HTMLElement ? trigger : null;
      showPanel(panel);
      dialog.showModal();
      document.documentElement.classList.add('sc-clovr-club-open');
    };

    const resetNativeSubmission = () => {
      if (!nativeSubmissionPrepared && !nativeSubmissionPending) return;
      const pendingKey = nativeSubmissionKey;
      const hadPendingMarker = nativeSubmissionPending;
      nativeSubmissionPrepared = false;
      nativeSubmissionPending = false;
      delete dialog.dataset.scClubSubmissionPending;
      if (hadPendingMarker && pendingKey) storage.remove(pendingKey);
      nativeSubmissionKey = null;
      returnFocus = pendingReturnFocus;
      pendingReturnFocus = null;
    };

    const close = () => {
      resetNativeSubmission();
      if (dialog.open) dialog.close();
    };

    const releaseNativeForm = (pendingKey, focusTarget = null) => {
      if (nativeSubmissionPrepared) return;
      nativeSubmissionPrepared = true;
      nativeSubmissionKey = pendingKey;
      dialog.dataset.scClubSubmissionPending = 'true';
      pendingReturnFocus = returnFocus;
      returnFocus = null;
      // Shopify's hCaptcha needs the native form to remain rendered, but a
      // modal dialog makes the rest of the page inert. Reopen the same dialog
      // non-modally so the form stays mounted while the platform challenge can
      // receive pointer and keyboard input. The native submit stays uncancelled.
      if (dialog.open) {
        ignoreNextCloseEvent = true;
        dialog.close();
      }
      if (typeof dialog.show === 'function') {
        dialog.show();
      } else {
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.remove('sc-clovr-club-open');
      if (focusTarget instanceof HTMLElement && focusTarget.isConnected) {
        focusTarget.focus({ preventScroll: true });
      }
    };

    const bindNativeForm = (form, pendingKey) => {
      if (!form) return;
      form.addEventListener('click', (event) => {
        const submitter = event.target.closest?.('button[type="submit"], input[type="submit"]');
        if (!submitter || submitter.form !== form || submitter.disabled) return;
        if (form.noValidate || submitter.formNoValidate || form.checkValidity()) {
          releaseNativeForm(pendingKey, submitter);
        }
      }, true);
      form.addEventListener('submit', () => {
        storage.set(pendingKey, dialog.id);
        nativeSubmissionPending = true;
        nativeSubmissionKey = pendingKey;
        // Direct requestSubmit()/script paths might not produce a button click.
        // Keep the submit-time conversion as a fallback, never as the primary
        // path seen by Shopify's document-capture submit listener.
        releaseNativeForm(pendingKey);
      });
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-sc-clovr-club-open]');
      if (!(trigger instanceof HTMLElement) || trigger.dataset.scClovrClubOpen !== dialog.id) return;
      event.preventDefault();
      applyDeliveryInterestFromTrigger(trigger);
      const requestedPanel = requestedClubPanel(trigger);
      if (requestedPanel) open(trigger, requestedPanel);
      else open(trigger);
    });

    root.querySelectorAll('[data-sc-club-close]').forEach((button) => {
      button.addEventListener('click', close);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('cancel', (event) => {
      if (!nativeSubmissionPrepared) return;
      event.preventDefault();
      close();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        if (!nativeSubmissionPrepared && !event.altKey && !event.ctrlKey && !event.metaKey) {
          containModalTab(dialog, event);
        }
        return;
      }
      if (!nativeSubmissionPrepared || event.key !== 'Escape') return;
      event.preventDefault();
      close();
    });

    dialog.addEventListener('close', () => {
      // close() queues this event in some browsers. Do not let that delayed
      // modal-close cleanup disturb the non-modal pending state just reopened
      // by releaseNativeForm().
      if (ignoreNextCloseEvent) {
        ignoreNextCloseEvent = false;
        return;
      }
      document.documentElement.classList.remove('sc-clovr-club-open');
      if (returnFocus?.isConnected) returnFocus.focus();
      returnFocus = null;
    });

    setupCountryInterest();
    bindNativeForm(newsletterForm, NEWSLETTER_PENDING);
    bindNativeForm(detailsForm, DETAILS_PENDING);

    const newsletterPending = storage.get(NEWSLETTER_PENDING) === dialog.id;
    const detailsPending = storage.get(DETAILS_PENDING) === dialog.id;
    const fromNewsletter = newsletterPending || window.location.hash === `#${newsletterForm?.id}`;
    const fromDetails = detailsPending || window.location.hash === `#${detailsForm?.id}`;
    const newsletterSucceeded = fromNewsletter && newsletterState?.dataset.success === 'true';
    const newsletterFailed = fromNewsletter && newsletterState?.dataset.errors === 'true';
    const detailsSucceeded = fromDetails && detailsState?.dataset.success === 'true';
    const detailsFailed = fromDetails && detailsState?.dataset.errors === 'true';

    if (newsletterPending) storage.remove(NEWSLETTER_PENDING);
    if (detailsPending) storage.remove(DETAILS_PENDING);

    if (newsletterSucceeded) {
      storage.set(JOINED, 'true');
      try {
        window.localStorage.setItem(WELCOME_POSTER_SUBSCRIBED, new Date().toISOString());
      } catch {
        // The existing poster also reads Shopify's native success state.
      }
      open(null, 'details');
      cleanResultUrl(newsletterForm.id, 'customer_posted');
    } else if (newsletterFailed) {
      open(null, 'signup');
      cleanResultUrl(newsletterForm.id, 'customer_posted');
    } else if (detailsSucceeded) {
      storage.set(JOINED, 'true');
      open(null, 'done');
      cleanResultUrl(detailsForm.id, 'contact_posted');
    } else if (detailsFailed) {
      open(null, 'details');
      cleanResultUrl(detailsForm.id, 'contact_posted');
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-sc-clovr-club]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
